import React, { useState, useMemo, useEffect } from "react";
import { DatePicker, Tabs, Button, Divider, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import "./SelectSlot.css";
import arrow from "../../../../assets/images/chevron-right.svg";
import greenRightIcon from "../../../../assets/images/tick.svg";
import { getAppointmentSlots } from "../../service";
import { InfoCircleOutlined } from "@ant-design/icons";

const { TabPane } = Tabs;
interface TimeSlot {
  start: string;
  end: string;
  status: string;
  appointments?: any[];
  leave?: any;
}

interface TimeSlotContainerProps {
  slots: TimeSlot[];
  selectedTimeSlot: TimeSlot | null;
  setSelectedTimeSlot: (slot: TimeSlot | null) => void;
  isLoading: boolean;
  handleConfirmAppointment: () => void;
  editTime: boolean;
  isSlotInPast: (time: string) => boolean;
  selectedDate: any;
  handleSelectedOptions: (slot: any, key: string) => void;
}

const TimeSlotContainer = ({
  slots,
  selectedTimeSlot,
  setSelectedTimeSlot,
  isLoading,
  handleConfirmAppointment,
  editTime,
  isSlotInPast,
  selectedDate,
  handleSelectedOptions,
}: TimeSlotContainerProps) => {
  if (isLoading) {
    return (
      <div>
        <Spin
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            zIndex: "9999",
          }}
          size="large"
        />
      </div>
    );
  }

  // Remove duplicate slots based on start+end time
  const uniqueSlots = Array.from(
    new Map(slots.map((slot) => [slot.start + slot.end, slot])).values()
  );

  const renderSlotsCount = (slots: TimeSlot[]) => {
    if (!slots || slots.length === 0) return "No slots available";
    const availableCount = slots.filter(
      (slot: TimeSlot) => slot.status === "available"
    ).length;
    const firstSlot = slots[0];
    const lastSlot = slots[slots.length - 1];
    const startTime = dayjs(firstSlot.start, "HH:mm:ss").format("hh:mm A");
    const endTime = dayjs(lastSlot.end, "HH:mm:ss").format("hh:mm A");
    return `${availableCount} Slots Available (${startTime} to ${endTime})`;
  };

  const getTooltipContent = (slot: TimeSlot, isInValid: boolean) => {
    // First check if slot is in the past
    if (isInValid) {
      return (
        <div className="past-slot-tooltip">
          <h4>Past Time Slot</h4>
          <div>
            This time slot has already passed and cannot be booked. Please
            select an available future slot for today's appointment.
          </div>
        </div>
      );
    }

    // Rest of your existing switch case for other slot statuses
    switch (slot.status) {
      case "unavailable":
        return (
          <div className="appointment-tooltip">
            {(slot.appointments?.length || 0) > 0 ? (
              <h4>Appointment Details({slot.appointments?.length || 0})</h4>
            ) : (
              <h4>No Availability Set</h4>
            )}
            <div
              className="appointments-scroll"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {slot.appointments?.map((appointment: any, index: number) => (
                <div
                  key={index}
                  className="appointment-patient-info d-flex align-items-center gap-2 p-2"
                >
                  <div className="appointment-patient-content d-flex align-items-center gap-1 me-2">
                    <i className="icon-profile" />
                    {appointment.pm_full_name.split(" ")[0].length > 10
                      ? `${appointment.pm_full_name
                          .split(" ")[0]
                          .substring(0, 10)}...`
                      : appointment.pm_full_name.split(" ")[0]}{" "}
                    ({appointment.pm_gender.charAt(0)}, {appointment.ageYears}y)
                  </div>
                  <div className="d-flex align-items-center gap-1 me-2">
                    <i className="icon-phone" />
                    {appointment.pm_contact_no}
                  </div>
                  <div className="d-flex align-items-center gap-1 me-2">
                    {dayjs(slot.start, "HH:mm:ss").format("hh:mm A")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case "leave":
        return (
          <div className="leave-tooltip">
            <div className="leave-tooltip">
              {slot.leave?.remarks ? (
                <>
                  <h4>On Leave</h4>
                  <div>{slot.leave?.remarks}</div>
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-center">
                  On Leave
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const isInvalidSlot = (slot: TimeSlot) => {
    console.log("slot", slot);
    const currentDateTime = dayjs();
    const slotDateTime = dayjs(selectedDate)
      .hour(dayjs(slot.start, "HH:mm:ss").hour())
      .minute(dayjs(slot.start, "HH:mm:ss").minute())
      .second(dayjs(slot.start, "HH:mm:ss").second())
      .add(10, "minute");

    return (
      currentDateTime.isAfter(slotDateTime) &&
      slot.status !== "confirmed" &&
      slot.status !== "unavailable" &&
      slot.status !== "leave"
    );
  };

  return (
    <>
      <div className="slots-info">{renderSlotsCount(slots)}</div>
      {editTime && (
        <div className="slot-instruction-info">{"Select new time slot"}</div>
      )}
      <div className="slots-container grid grid-cols-3 gap-3 mt-3 justify-center mx-auto">
        {uniqueSlots.length === 0 ? (
          <div className="col-span-3 text-center text-gray-400">
            No slots available
          </div>
        ) : (
          uniqueSlots.map((slot, idx) => {
            const isInValid = isInvalidSlot(slot);
            return (
              <Tooltip
                key={slot.start}
                title={getTooltipContent(slot, isInValid)}
                overlayClassName="slot-tooltip"
                placement="top"
              >
                <button
                  className={`rounded-xl border px-1 py-2 font-medium transition-all flex items-center justify-center gap-1 w-[98px]
                  ${
                    slot.status === "confirmed"
                      ? "bg-[#e6f4ea] text-[#1e8e3e] cursor-not-allowed"
                      : selectedTimeSlot?.start === slot.start
                      ? "bg-[#564DD5] text-white border-[#564DD5]"
                      : "bg-white text-[#564DD5] border-[#E0DEFF]"
                  } ${
                    isInValid ||
                    slot.status === "unavailable" ||
                    slot.status === "leave"
                      ? "cursor-not-allowed opacity-25"
                      : "cursor-pointer"
                  }
                `}
                  onClick={() => {
                    setSelectedTimeSlot(slot);
                    handleSelectedOptions(
                      `I want to book an appointment for ${dayjs(
                        selectedDate
                      ).format("DD MMM YY")} ${dayjs(
                        slot.start,
                        "HH:mm:ss"
                      ).format("hh:mm A")} - ${dayjs(
                        slot.end,
                        "HH:mm:ss"
                      ).format("hh:mm A")}`,
                      "slots"
                    );
                  }}
                  disabled={
                    isInValid ||
                    slot.status === "confirmed" ||
                    slot.status === "unavailable" ||
                    slot.status === "leave"
                  }
                >
                  {slot.status === "confirmed" && (
                    <img
                      src={greenRightIcon}
                      alt="green-right-icon"
                      className="w-4 h-4"
                    />
                  )}
                  {slot.status === "unavailable" || slot.status === "leave" ? (
                    <>
                      {dayjs(slot.start, "HH:mm:ss").format("hh:mm A")} -{" "}
                      {dayjs(slot.end, "HH:mm:ss").format("hh:mm A")}
                      <InfoCircleOutlined className="info-icon" />
                    </>
                  ) : (
                    dayjs(slot.start, "HH:mm:ss").format("hh:mm A")
                  )}
                </button>
              </Tooltip>
            );
          })
        )}
      </div>
    </>
  );
};

export default function SelectSlot({
  handleSelectedOptions,
  selectedDoctor,
  agentDetails,
}: any) {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs()); // Track start date for chips
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(
    null
  );
  const [timeSlots, setTimeSlots] = useState<any>({});

  const getCurrentTimeSection = () => {
    const currentHour = dayjs().hour();

    if (currentHour >= 0 && currentHour < 3) return "MIDNIGHT";
    if (currentHour >= 3 && currentHour < 5) return "EARLY_MORNING";
    if (currentHour >= 5 && currentHour < 12) return "MORNING";
    if (currentHour >= 12 && currentHour < 17) return "AFTERNOON";
    if (currentHour >= 17 && currentHour < 21) return "EVENING";
    if (currentHour >= 21 && currentHour <= 23) return "NIGHT";

    return "MORNING";
  };

  const [activeTab, setActiveTab] = useState(getCurrentTimeSection());

  useEffect(() => {
    handleSelectDate(dayjs());
  }, []);

  // Generate date chips based on startDate
  const dateChips = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => startDate.clone().add(i, "day"));
  }, [startDate]);

  // Handle navigation
  const handlePrevious = () => {
    setStartDate((prev) => prev.subtract(5, "day"));
  };

  const handleNext = () => {
    setStartDate((prev) => prev.add(5, "day"));
  };

  // Check if we can go back (disable if we're at today)
  const canGoPrevious = !startDate.isSame(dayjs(), "day");

  const TIME_SECTIONS_CONFIG = [
    { key: "MIDNIGHT", label: "Midnight", timeRange: "12AM - 3AM" },
    { key: "EARLY_MORNING", label: "Early Morning", timeRange: "3AM - 5AM" },
    { key: "MORNING", label: "Morning", timeRange: "5AM - 12PM" },
    { key: "AFTERNOON", label: "Afternoon", timeRange: "12PM - 5PM" },
    { key: "EVENING", label: "Evening", timeRange: "5PM - 9PM" },
    { key: "NIGHT", label: "Night", timeRange: "9PM - 12AM" },
  ];

  const getTimeSection = (time: any) => {
    const hour = dayjs(time, "HH:mm:ss").hour();

    if (hour >= 0 && hour < 3) return "MIDNIGHT";
    if (hour >= 3 && hour < 5) return "EARLY_MORNING";
    if (hour >= 5 && hour < 12) return "MORNING";
    if (hour >= 12 && hour < 17) return "AFTERNOON";
    if (hour >= 17 && hour < 21) return "EVENING";
    if (hour >= 21 && hour <= 23) return "NIGHT";

    return "MORNING"; // default fallback
  };

  const getFirstAvailableSection = (timeSlots: any) => {
    const sections = [
      "MIDNIGHT",
      "EARLY_MORNING",
      "MORNING",
      "AFTERNOON",
      "EVENING",
      "NIGHT",
    ];

    for (const section of sections) {
      const sectionSlots = timeSlots[section];
      if (sectionSlots && sectionSlots.length > 0) {
        // Check if section has valid slots (not just unavailable slots without appointments)
        const hasValidSlots = sectionSlots.some(
          (slot: any) =>
            slot.status !== "unavailable" ||
            (slot.appointments && slot.appointments.length > 0)
        );
        if (hasValidSlots) {
          return section;
        }
      }
    }
    return "MORNING"; // default fallback
  };

  // Helper function to find the next available section from current time
  const getNextAvailableSection = (timeSlots: any, currentSection: any) => {
    const sections = [
      "MIDNIGHT",
      "EARLY_MORNING",
      "MORNING",
      "AFTERNOON",
      "EVENING",
      "NIGHT",
    ];
    const currentIndex = sections.indexOf(currentSection);

    // First check if current section has slots
    if (timeSlots[currentSection]?.length > 0) {
      const hasValidSlots = timeSlots[currentSection].some(
        (slot: any) =>
          slot.status !== "unavailable" ||
          (slot.appointments && slot.appointments.length > 0)
      );
      if (hasValidSlots) return currentSection;
    }

    // Look for next available section
    for (let i = currentIndex + 1; i < sections.length; i++) {
      const section = sections[i];
      if (timeSlots[section]?.length > 0) {
        const hasValidSlots = timeSlots[section].some(
          (slot: any) =>
            slot.status !== "unavailable" ||
            (slot.appointments && slot.appointments.length > 0)
        );
        if (hasValidSlots) return section;
      }
    }

    return "MORNING"; // default fallback
  };

  const generateTimeSlots = (slotsData: any) => {
    let allTimeSlots: any = {
      MIDNIGHT: [],
      EARLY_MORNING: [],
      MORNING: [],
      AFTERNOON: [],
      EVENING: [],
      NIGHT: [],
    };

    slotsData.forEach((slot: any) => {
      // Just add type property to distinguish in UI
      const timeSlot = {
        ...slot,
      };

      // Add the slot to appropriate section
      const section = getTimeSection(slot.start);
      allTimeSlots[section].push(timeSlot);
    });

    // Sort slots within each section
    Object.keys(allTimeSlots).forEach((section) => {
      allTimeSlots[section].sort((a: any, b: any) => {
        return dayjs(a.start, "HH:mm:ss").diff(dayjs(b.start, "HH:mm:ss"));
      });
    });

    return allTimeSlots;
  };

  const handleSelectDate = async (date: any) => {
    setSelectedDate(date);
    setStartDate(date.startOf("day"));
    const response: any = await getAppointmentSlots({
      um_id: selectedDoctor?.um_id,
      hm_id: agentDetails?.hm_id || selectedDoctor?.hm_id,
      hm_business_id: agentDetails?.hm_business_id,
      appointment_date: date.format("YYYY-MM-DD"),
    });
    if (response?.length > 0) {
      const generatedSlots = generateTimeSlots(response || []);
      setTimeSlots(generatedSlots);

      // Determine which tab should be active
      if (dayjs(selectedDate).isSame(dayjs(), "day")) {
        // For current date
        const currentSection = getCurrentTimeSection();
        setActiveTab(getNextAvailableSection(generatedSlots, currentSection));
      } else {
        // For past or future dates
        setActiveTab(getFirstAvailableSection(generatedSlots));
      }
    } else {
      setTimeSlots({
        MIDNIGHT: [],
        EARLY_MORNING: [],
        MORNING: [],
        AFTERNOON: [],
        EVENING: [],
        NIGHT: [],
      });
    }
  };

  return (
    <div
      className="appointment-wrap max-w-[450px] mx-auto"
      style={{
        border: "1.5px solid #E0DEFF",
        borderRadius: 24,
        background: "#fff",
        boxShadow: "0 2px 16px 0 rgba(86,77,213,0.06)",
      }}
    >
      {/* Doctor Info */}
      <div className="px-6 pb-0 pt-2 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <div className="text-sm font-semibold">
            {selectedDoctor?.doctor_name}
          </div>
          <div className="text-xs">{selectedDoctor?.speciality}</div>
        </div>
      </div>

      <Divider className="my-2" />

      <div className="px-6">
        {/* Date Picker Row */}
        <div className="flex justify-between items-center mb-4">
          <DatePicker
            value={selectedDate}
            onChange={(date) => {
              if (date) {
                setSelectedDate(date);
                setStartDate(date.startOf("day")); // Update chip range to include selected date
              }
            }}
            format="MMMM, YYYY"
            picker="date"
            inputReadOnly
            allowClear={false}
            style={{ width: 130 }}
            suffixIcon={
              <img src={arrow} alt="arrow" className="w-6 h-6 rotate-90" />
            }
          />
          <div style={{ display: "flex", gap: 5 }}>
            <Button
              className="rounded-lg w-7"
              size="small"
              onClick={handlePrevious}
              disabled={!canGoPrevious}
            >
              <img src={arrow} alt="arrow" className="w-7 h-7 rotate-180" />
            </Button>
            <Button
              className="rounded-lg w-7"
              size="small"
              onClick={handleNext}
            >
              <img src={arrow} alt="arrow" className="w-7 h-7" />
            </Button>
          </div>
        </div>

        {/* Date Chips */}
        <div className="flex gap-3 overflow-x-auto">
          {dateChips.map((date) => {
            const isSelected = selectedDate.isSame(date, "day");
            const isToday = date.isSame(dayjs(), "day");
            return (
              <div
                key={date.format("YYYY-MM-DD")}
                className="flex flex-col items-center"
              >
                <div
                  onClick={() => handleSelectDate(date)}
                  className={`
                  ${isSelected ? "bg-[#564DD5] text-white" : "bg-[#F4F3FF]"}
                  rounded-xl w-11 h-9 flex items-center justify-center font-medium cursor-pointer transition-all
                `}
                >
                  {date.format("D")}
                </div>
                <div
                  className={`
                  mt-1 text-xs text-center
                  ${isSelected ? "text-[#564DD5] font-medium" : "font-medium"}
                `}
                >
                  {isToday ? "Today" : date.format("ddd")}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Divider className="my-2" />

      <div className="px-3 pb-6 pt-0 overflow-y-auto h-[300px]">
        {/* Time Slot Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarGutter={16}
          tabBarStyle={{ overflowX: "auto", whiteSpace: "nowrap" }}
          moreIcon={null}
        >
          {TIME_SECTIONS_CONFIG.map((section) => (
            <TabPane
              key={section.key}
              tab={section.label}
              className="text-sm font-medium"
            >
              <TimeSlotContainer
                slots={timeSlots[section.key] || []}
                selectedTimeSlot={selectedTimeSlot}
                setSelectedTimeSlot={setSelectedTimeSlot}
                isLoading={false}
                handleConfirmAppointment={() => {}}
                editTime={false}
                isSlotInPast={() => false}
                selectedDate={selectedDate}
                handleSelectedOptions={handleSelectedOptions}
              />
            </TabPane>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
